/**
 * The brand's signature shapes (SPEC §17.3): sunset arcs, the three-stripe band, arched
 * frames, photo placeholders and handwritten host notes. Soft and solid — no black outlines,
 * no hard offset shadows (SPEC §17: stay distinct from Happy Guest).
 */
import type { CSSProperties, ReactNode } from 'react';
import { isPlaceholder } from '../placeholders';

const ARC_COLORS = ['var(--rust)', 'var(--orange)', 'var(--mustard)', 'var(--pine)'] as const;

export interface SunsetArcsProps {
  /** How many concentric arcs, outermost first (rust → orange → mustard → pine). */
  rings?: 3 | 4;
  /** Draw the rising sun disc in the middle (used on celebratory screens). */
  sun?: boolean;
  /** Animate the sun rising (honors prefers-reduced-motion). */
  rise?: boolean;
  width?: number;
  strokeWidth?: number;
  style?: CSSProperties;
  className?: string;
}

/** Concentric half-circle arcs, drawn as inline SVG so they scale crisply. */
export function SunsetArcs({
  rings = 4,
  sun = false,
  rise = false,
  width = 200,
  strokeWidth = 16,
  style,
  className,
}: SunsetArcsProps) {
  const r0 = 100 - strokeWidth / 2 - 2;
  const step = strokeWidth + 2;
  return (
    <svg
      width={width}
      height={width / 2}
      viewBox="0 0 200 100"
      aria-hidden="true"
      className={className}
      style={style}
    >
      <g fill="none" strokeWidth={strokeWidth}>
        {ARC_COLORS.slice(0, rings).map((color, i) => {
          const r = r0 - i * step;
          return <path key={color} d={`M${100 - r} 100 A${r} ${r} 0 0 1 ${100 + r} 100`} stroke={color} />;
        })}
      </g>
      {sun && (
        <circle
          cx="100"
          cy="100"
          r={Math.max(r0 - rings * step + strokeWidth / 2 - 4, 14)}
          fill="var(--mustard-tint)"
          className={rise ? 'pmh-sunrise' : undefined}
        />
      )}
    </svg>
  );
}

/** Rust, orange and mustard stripes, 4px each: sits above the dark bottom nav. */
export function StripeBand({ thickness = 4, gap = 0 }: { thickness?: number; gap?: number }) {
  return (
    <div className="pmh-stripes" style={{ gap }} aria-hidden="true">
      <div style={{ height: thickness, background: 'var(--rust)', borderRadius: gap ? 2 : 0 }} />
      <div style={{ height: thickness, background: 'var(--orange)', borderRadius: gap ? 2 : 0 }} />
      <div style={{ height: thickness, background: 'var(--mustard)', borderRadius: gap ? 2 : 0 }} />
    </div>
  );
}

export type Tint = 'pine' | 'rust' | 'mustard' | 'cream';

const TINTS: Record<Tint, { bg: string; fg: string }> = {
  pine: { bg: 'var(--pine-tint)', fg: 'var(--pine)' },
  rust: { bg: 'var(--rust-tint)', fg: 'var(--ink-muted)' },
  mustard: { bg: 'var(--mustard-tint)', fg: 'var(--ink-muted)' },
  cream: { bg: 'var(--surface)', fg: 'var(--ink-muted)' },
};

export interface ArchFrameProps {
  width: number | string;
  height: number | string;
  tint?: Tint;
  /** Real photo, once Rook supplies one. */
  src?: string | null;
  alt?: string;
  /** Bracketed label shown until there's a photo, e.g. "[Photo: the house]". */
  label?: string;
  /** Cream border, as on hero arches. */
  border?: number;
  /** Bottom corner radius. 0 = the arch sits on a baseline. */
  baseRadius?: number;
  children?: ReactNode;
  style?: CSSProperties;
}

/** Round-topped arch, like lodge windows and mine portals. */
export function ArchFrame({
  width,
  height,
  tint = 'pine',
  src,
  alt = '',
  label = '[Photo]',
  border = 0,
  baseRadius = 8,
  children,
  style,
}: ArchFrameProps) {
  const t = TINTS[tint];
  return (
    <div
      className="pmh-arch"
      style={{
        width,
        height,
        background: t.bg,
        color: t.fg,
        border: border ? `${border}px solid var(--surface)` : undefined,
        borderBottom: border && baseRadius === 0 ? 0 : undefined,
        borderRadius: `999px 999px ${baseRadius}px ${baseRadius}px`,
        ...style,
      }}
    >
      {src ? <img src={src} alt={alt} /> : <span className="pmh-arch__label">{label}</span>}
      {children}
    </div>
  );
}

/** A labelled stand-in until Rook supplies real photos (SPEC §17.4, §17.5). */
export function PhotoPlaceholder({
  label = '[Photo]',
  tint = 'rust',
  width = 52,
  height = 62,
}: {
  label?: string;
  tint?: Tint;
  width?: number;
  height?: number;
}) {
  return (
    <ArchFrame
      width={width}
      height={height}
      tint={tint}
      label={label}
      style={{ flex: 'none', fontSize: 10 }}
    />
  );
}

/** A short handwritten aside from the hosts. Use at most one per screen (SPEC §17.2). */
export function HostNote({
  children,
  color = 'rust',
  size = 23,
  as: Tag = 'p',
}: {
  children: ReactNode;
  color?: 'rust' | 'pine' | 'mustard-tint' | 'cream';
  size?: number;
  as?: 'p' | 'span';
}) {
  const colors = {
    rust: 'var(--rust)',
    pine: 'var(--pine)',
    'mustard-tint': 'var(--mustard-tint)',
    cream: 'var(--bg)',
  };
  return (
    <Tag className="pmh-note" style={{ color: colors[color], fontSize: size }}>
      {children}
    </Tag>
  );
}

/** Round wobbling badge ("no chores!", "our fave!"). */
export function Badge({
  children,
  size = 88,
  style,
}: {
  children: ReactNode;
  size?: number;
  style?: CSSProperties;
}) {
  return (
    <div className="pmh-badge" style={{ width: size, height: size, ...style }}>
      {children}
    </div>
  );
}

/** Text that renders bracketed placeholders visibly, and marks them for assistive tech. */
export function Fact({ value, suffix }: { value: string | number | null | undefined; suffix?: string }) {
  const text = value === null || value === undefined || value === '' ? '[NOT SET]' : String(value);
  const placeholder = isPlaceholder(text);
  return (
    <span className={placeholder ? 'pmh-placeholder' : undefined} data-placeholder={placeholder || undefined}>
      {text}
      {suffix ? ` ${suffix}` : ''}
    </span>
  );
}
